import { inject, Injectable, Injector } from '@angular/core'
import {
    ActivatedRouteSnapshot,
    DetachedRouteHandle,
    NavigationEnd,
    NavigationStart,
    Route,
    Router,
    RouteReuseStrategy,
} from '@angular/router'

/**
 * Catch-all "back returns the user to where they were" support for list /
 * dashboard pages.
 *
 * A route opts in by declaring `data: { reuseList: true }` on its config.
 * When the user leaves a flagged route (to a detail page, or anywhere else),
 * we detach the component instead of destroying it. When the user comes back
 * via the browser-back stack (the shell back button uses `Location.back()`,
 * which dispatches popstate), we re-attach the same instance — its signals,
 * inner scroll positions, table state, and pending subscriptions are all the
 * way they left them.
 *
 * Two things are preserved by detach alone, and one thing isn't:
 *   - Preserved: component fields, signals, RxJS subscriptions, the DOM tree
 *     itself (the view stays alive off-screen).
 *   - NOT preserved: scrollTop on inner-scroll containers. Removing the view
 *     host from the DOM and re-inserting it triggers a fresh layout pass that
 *     clamps scrollTop back to 0. We work around this by snapshotting the
 *     scrollTops on every NavigationStart and replaying them after the
 *     matching NavigationEnd.
 *
 * Eviction rules (matches the user-facing spec):
 *   - Popstate to a cached route → attach. The cache entry stays in place;
 *     Angular calls shouldAttach/retrieve multiple times in a single
 *     navigation, so consuming on retrieve would make the second call miss.
 *     The next leave's store() overwrites the same slot.
 *   - Imperative nav to a flagged route (top-level nav link click, deep link,
 *     router.navigate from app code) → load fresh. The handle is NOT evicted,
 *     because the next detach overwrites the same cache slot.
 *   - TTL — handles older than 15 minutes are dropped on next retrieve. Long-
 *     idle tabs come back to a fresh page rather than a possibly-stale one.
 */

// NavigationTrigger isn't exported from @angular/router; inline the union here.
type NavigationTrigger = 'imperative' | 'popstate' | 'hashchange'

interface CachedHandle {
    handle: DetachedRouteHandle
    storedAt: number
    /** Map of CSS selector → scrollTop, captured right before the view was detached. */
    scrolls: Map<string, number>
}

const TTL_MS = 15 * 60 * 1000
const MAX_CACHED_HANDLES = 2

// Selectors for scroll containers Kartorium list pages use. Adding a new
// pattern here is the only step required to extend scroll preservation —
// we walk the document for each one and snapshot every match's scrollTop.
const SCROLL_CONTAINER_SELECTORS: readonly string[] = [
    '.page-content',
    '.content-container',
    '.table-scroll',
]

@Injectable({ providedIn: 'root' })
export class ListPageReuseStrategy implements RouteReuseStrategy {
    private _cache = new Map<Route, CachedHandle>()
    /**
     * Scrolls captured from the live DOM at the most recent NavigationStart.
     * Consumed by store() so the leaving page's scroll positions get packed
     * into its cache entry. Distinct from _replayScrolls — that one is for
     * the entering side of a back-nav.
     */
    private _capturedScrolls: Map<string, number> | null = null
    /**
     * Scrolls that retrieve() loaded from a cache entry, queued for replay
     * after the matching NavigationEnd. The capture and replay sides used
     * to share one field; the result was that NavigationStart's capture
     * overwrote retrieve's replay (and a stray null-clear before replay
     * killed it outright). Keeping them separate makes the two paths
     * independent and safe under any interleaving.
     */
    private _replayScrolls: Map<string, number> | null = null

    // Router → RouteReuseStrategy → Router would be a circular constructor
    // dependency, so we capture the Injector and resolve Router lazily on
    // first use. By the time any strategy method runs, the Router has been
    // constructed and is safe to fetch.
    private readonly _injector = inject(Injector)
    private _router: Router | null = null

    private _getRouter(): Router {
        if (!this._router) {
            this._router = this._injector.get(Router)
            this._wireRouter(this._router)
        }
        return this._router
    }

    private _wireRouter(router: Router): void {
        router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                // Right before a navigation runs, snapshot the scrollTop of
                // every recognized scroll container in the live DOM. If the
                // current route is flagged-and-about-to-be-detached, store()
                // will pull this snapshot into its cache entry.
                this._capturedScrolls = this._snapshotScrolls()
            }
            if (event instanceof NavigationEnd) {
                // _capturedScrolls only mattered for the just-completed leave;
                // store() has already consumed it (or it wasn't relevant).
                this._capturedScrolls = null
                // If retrieve() loaded scrolls from a cached entry, replay
                // them onto the now-attached DOM. Scheduled on the next
                // animation frame so layout has settled.
                this._scheduleScrollRestore()
            }
        })
    }

    private _currentTrigger(): NavigationTrigger | null {
        return this._getRouter().getCurrentNavigation()?.trigger ?? null
    }

    shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
        return future.routeConfig === curr.routeConfig
    }

    shouldDetach(route: ActivatedRouteSnapshot): boolean {
        return this._isReusable(route)
    }

    store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
        const key = route.routeConfig
        if (!key || !this._isReusable(route)) return
        if (!handle) {
            this._cache.delete(key)
            return
        }
        if (this._cache.has(key)) {
            this._cache.delete(key)
        }
        this._cache.set(key, {
            handle,
            storedAt: Date.now(),
            scrolls: this._capturedScrolls ?? new Map(),
        })
        this._evictIfNeeded()
    }

    shouldAttach(route: ActivatedRouteSnapshot): boolean {
        if (!this._isReusable(route)) return false
        const key = route.routeConfig!
        if (this._currentTrigger() !== 'popstate') return false
        const cached = this._cache.get(key)
        if (!cached) return false
        if (Date.now() - cached.storedAt > TTL_MS) {
            this._cache.delete(key)
            this._destroyDetachedHandle(cached.handle)
            return false
        }
        return true
    }

    retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
        if (!this._isReusable(route)) return null
        const cached = this._cache.get(route.routeConfig!)
        if (!cached) return null
        // Do NOT consume the cache here — Angular calls retrieve/shouldAttach
        // multiple times during a single navigation. The cache entry stays
        // until store() overwrites it on the next leave.
        //
        // Unconditionally overwrite _replayScrolls. Repeated retrieves in the
        // same navigation just rewrite the same value; the field is independent
        // of _capturedScrolls so there's no risk of the leaving page's
        // snapshot stomping on it.
        this._replayScrolls = cached.scrolls
        return cached.handle
    }

    private _isReusable(route: ActivatedRouteSnapshot): boolean {
        return !!route.routeConfig?.data?.['reuseList']
    }

    /** Capture scrollTop on every recognized scroll container currently in the DOM. */
    private _snapshotScrolls(): Map<string, number> {
        const out = new Map<string, number>()
        for (const selector of SCROLL_CONTAINER_SELECTORS) {
            const nodes = document.querySelectorAll<HTMLElement>(selector)
            for (let i = 0; i < nodes.length; i++) {
                const node = nodes[i]
                if (node.scrollTop > 0) {
                    out.set(`${selector}::${i}`, node.scrollTop)
                }
            }
        }
        return out
    }

    private _scheduleScrollRestore(): void {
        const scrolls = this._replayScrolls
        if (!scrolls || scrolls.size === 0) {
            this._replayScrolls = null
            return
        }
        this._replayScrolls = null
        // Two rAFs — Angular needs one frame to attach the view and another
        // to lay it out before scrollTop assignment will stick.
        requestAnimationFrame(() => requestAnimationFrame(() => {
            for (const [key, scrollTop] of scrolls) {
                const [selector, indexStr] = key.split('::')
                const idx = Number(indexStr)
                const nodes = document.querySelectorAll<HTMLElement>(selector)
                const node = nodes[idx]
                if (node) node.scrollTop = scrollTop
            }
        }))
    }

    /**
     * Keep only the most recent reusable list pages in memory.
     *
     * These routes preserve component instances, timers, and DOM trees while
     * detached. Capping the cache prevents detached dashboards from piling up
     * when the user moves across several heavy OEE pages in one session.
     */
    private _evictIfNeeded(): void {
        while (this._cache.size > MAX_CACHED_HANDLES) {
            let oldestKey: Route | null = null
            let oldestStoredAt = Number.POSITIVE_INFINITY
            for (const [key, cached] of this._cache.entries()) {
                if (cached.storedAt < oldestStoredAt) {
                    oldestStoredAt = cached.storedAt
                    oldestKey = key
                }
            }

            if (!oldestKey) {
                return
            }

            const cached = this._cache.get(oldestKey)
            this._cache.delete(oldestKey)
            this._destroyDetachedHandle(cached?.handle)
        }
    }

    private _destroyDetachedHandle(handle: DetachedRouteHandle | undefined): void {
        const maybeHandle = handle as {
            componentRef?: { destroy?: () => void }
            destroy?: () => void
        } | undefined

        maybeHandle?.componentRef?.destroy?.()
        maybeHandle?.destroy?.()
    }
}
